import { DatePipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeNL from '@angular/common/locales/nl';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { ToastrModule } from 'ngx-toastr';

import {
  ApplicationInsightsService,
  BUILD_NO,
  EnvService,
  envServiceProvider,
  NotificationService,
  PreloaderServiceBase,
  SeahorseTranslateModule,
} from '@seahorse/common';
import { ContentMappingModule } from './content-mapping/content-mapping.module';
import { AccountModule } from './core/account/account.module';
import { CoreModule } from './core/core.module';
import { IdentityService } from './core/services/identity.service';
import { CustomerAppRoutingModule } from './customer-app-routing.module';
import { CustomerAppComponent } from './customer-app.component';
import { GenericUIModule } from './generic-ui/generic-ui.module';
import { MapModule } from './map/map.module';
import { PreferenceModule } from './preference/preference.module';
import { CaEnvService } from './shared/services/env.service';
import { registerLicense } from '@syncfusion/ej2-base';
import { SEAHORSE_BUILD_NO } from '../build';
import { ProxyServices } from './core/services/proxy.service';
import {
  BaseMapDataService,
  ProxyService,
  UsersDataService,
} from '@seahorse/domain';
import { UserDataService } from './user/services/user-data.service';
import {
  BaseFilesCardService,
  CUSTOM_FORM_CONTROLS,
  EXPENSE_CREATE_MODAL,
  UI_MODULE_CUSTOM_COMPONENTS,
} from '@seahorse/temp';
import { FileListCardService } from './layout/components/file-list-card/file-list-card.service';
import { uiModuleCustomComponents } from './custom-modules/models/pages-overrides';
import { AppInitializerService } from './core/services/app-initializer.service';
import {
  IdentityServiceBase,
  authRequestInterceptor,
  responseInterceptor,
  AUTH_ROUTES,
  AccountServiceBase,
} from '@seahorse/auth';
import { controlFactory } from './core/models/form-control-factory';
import { SeahorseAccountService } from './core/account/services/user-account.service';
import { PreloaderService } from './core/services/preloader.service';
import { ToastrNotificationService } from '@seahorse/ui';
import { MarkdownModule } from 'ngx-markdown';
import { ExpenseDialogComponent } from './expenses/components/expense-dialog/expense-dialog.component';
import { MapDataService } from './map/services/map-data.service';
import { NuMonacoEditorModule } from '@ng-util/monaco-editor';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeng/themes/lara';
import { definePreset } from '@primeng/themes';

export function initializeApp(appInitializerService: AppInitializerService) {
  registerLocaleData(localeNL, 'nl-NL');
  return () => {
    return appInitializerService.Init();
  };
}

const LaraBlue = definePreset(Lara, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});

@NgModule({
  declarations: [CustomerAppComponent],
  imports: [
    FormsModule,
    NgbModule,
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      closeButton: true,
      newestOnTop: true,
      progressBar: true,
      positionClass: 'toast-bottom-right',
    }),
    CoreModule,
    SeahorseTranslateModule.forRoot(),
    ReactiveFormsModule,
    MarkdownModule.forRoot(),

    AccountModule,
    ContentMappingModule,
    PreferenceModule,
    GenericUIModule,

    CustomerAppRoutingModule, // Has to be at the end for wild card to work
    MapModule, // ensure other modules are loaded before import the MapModule
    NuMonacoEditorModule.forRoot(),
  ],
  providers: [
    AppInitializerService,
    ApplicationInsightsService,
    DatePipe,
    envServiceProvider(CaEnvService),
    provideHttpClient(
      withInterceptors([authRequestInterceptor, responseInterceptor])
    ),
    { provide: EnvService, useExisting: CaEnvService },
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(AppInitializerService));
      return initializerFn();
    }),
    { provide: BUILD_NO, useValue: SEAHORSE_BUILD_NO },
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    providePrimeNG({
      theme: {
        preset: LaraBlue,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    {
      provide: NotificationService,
      useFactory: () => inject(ToastrNotificationService),
    },
    { provide: ProxyService, useFactory: () => inject(ProxyServices) },
    { provide: UsersDataService, useFactory: () => inject(UserDataService) },
    {
      provide: PreloaderServiceBase,
      useFactory: () => inject(PreloaderService),
    },
    {
      provide: BaseFilesCardService,
      useFactory: () => inject(FileListCardService),
    },
    {
      provide: BaseMapDataService,
      useFactory: () => inject(MapDataService),
    },
    {
      provide: IdentityServiceBase,
      useFactory: () => inject(IdentityService),
    },
    {
      provide: AccountServiceBase,
      useFactory: () => inject(SeahorseAccountService),
    },
    {
      provide: UI_MODULE_CUSTOM_COMPONENTS,
      useValue: uiModuleCustomComponents,
    },
    {
      provide: CUSTOM_FORM_CONTROLS,
      useValue: controlFactory,
    },
    {
      provide: AUTH_ROUTES,
      useValue: {
        login: 'account/login',
        unauthorized: 'not-authorized',
      },
    },
    { provide: EXPENSE_CREATE_MODAL, useValue: ExpenseDialogComponent },
  ],
  bootstrap: [CustomerAppComponent],
})
export class CustomerAppModule {
  constructor(_envService: CaEnvService) {
    // Registering Syncfusion license key
    registerLicense(_envService.sfKey);
  }
}
